import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../components/apiconfig/api-config';
import ctmsLogo from '../../assets/ctms-logo.png';
import {
  FaHome,
  FaFileAlt,
  FaCalculator,
  FaShippingFast, FaFileContract,
  FaHandshake, FaShoppingCart, FaTruck,
  FaIndustry, FaSeedling, FaUserCog, FaCogs, 
  FaBriefcase, FaUserFriends, FaCreditCard, FaCertificate,
  FaExchangeAlt, FaBox, FaDatabase,
  FaChevronDown, FaChevronRight, FaTimes, FaSignOutAlt
} from 'react-icons/fa';

const scrollbarStyles = `
  .custom-scrollbar,
  .custom-scrollbar * {
    scrollbar-width: thin;
    scrollbar-color: #e2e8f0 transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 10px;
  }
`;

const groups = [
  {
    title: 'EXECUTION',
    items: [
      { name: 'Dashboard', icon: <FaHome />, path: '/dashboard' },
    ]
  },
  {
    title: 'PURCHASE (Khareed)',
    items: [
      { name: 'Purchase Contract', icon: <FaFileContract />, path: '/purchase-contract' },
    ]
  },
  {
    title: 'WAREHOUSE (Godown)',
    items: [
      { name: 'Warehouse Entry', icon: <FaBox />, path: '/warehouse-entry' },
      { name: 'Warehouse Transfer', icon: <FaExchangeAlt />, path: '/warehouse-transfer' },
    ]
  },
  {
    title: 'SALES (Bikri)',
    items: [
      { name: 'Sales Order', icon: <FaShoppingCart />, path: '/sales-order' },
      { name: 'Delivery Order', icon: <FaTruck />, path: '/delivery-order' },
      { name: 'Dispatch Entry', icon: <FaShippingFast />, path: '/dispatch-entry' },
    ]
  },
  {
    title: 'FINANCE & PAYMENTS',
    items: [
      { name: 'Cost Breakup', icon: <FaCalculator />, path: '/cost-breakup' },
      { name: 'Payment Entry', icon: <FaCreditCard />, path: '/payment' },
    ]
  },
  {
    title: 'REPORTS',
    subitems: [
      { name: 'Purchase Summary Report', icon: <FaFileAlt />, path: '/report/purchase-summary' },
      { name: 'Sales Report', icon: <FaFileContract />, path: '/report/sales' },
    ],
  },
  {
    title: 'SYSTEM & MASTERS',
    items: [
      {
        name: 'Masters',
        icon: <FaCogs />,
        subitems: [
          { name: 'Supplier', icon: <FaIndustry />, path: '/supplier' },
          { name: 'Company', icon: <FaIndustry />, path: '/company' },
          { name: 'Buyer', icon: <FaUserFriends />, path: '/buyer' },
          { name: 'Commodity', icon: <FaSeedling />, path: '/commodity' },
          { name: 'Purchase Broker', icon: <FaHandshake />, path: '/purchase-broker' },
          { name: 'Sale Broker', icon: <FaBriefcase />, path: '/broker' },
          { name: 'CMA / Transporter', icon: <FaUserCog />, path: '/cma' },
          { name: 'Document', icon: <FaFileAlt />, path: '/document' },
          { name: 'Issuing Authorities', icon: <FaCertificate />, path: '/issuing-authorities' },
        ]
      },
      { name: 'Backup Management', icon: <FaDatabase />, path: '/backup-management' },
    ]
  }
];

const Sidebar = ({ open, onClose, isMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [userName, setUserName] = useState('User');
  const [userRole, setUserRole] = useState('User');
  
  const [openGroup, setOpenGroup] = useState({});

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/profile/profile-get`,
          { withCredentials: true }
        );
        if (response.data.success) {
          setUserName(response.data.data.name);
          setUserRole(response.data.data.role);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, []);

  const isPathActive = useCallback((path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    if (location.pathname.startsWith(path)) {
      const nextChar = location.pathname.charAt(path.length);
      return nextChar === '/' || nextChar === '' || nextChar === '?';
    }
    return false;
  }, [location.pathname]);

  const handleCollapse = (key) => {
    setOpenGroup(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      await axios.post(
        `${API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      toast.success('Logged out successfully!', { position: 'top-right', autoClose: 2000, theme: 'colored' });
      if (isMobile) onClose();
      setTimeout(() => navigate('/'), 1000);
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      toast.error('Logout failed, but tokens cleared locally', { position: 'top-right', autoClose: 3000, theme: 'colored' });
      setTimeout(() => navigate('/'), 2000);
    } finally {
      setLogoutLoading(false);
    }
  };

  const renderMenuItems = (items, parentKey = '', level = 0) => {
    return (
      <ul className="flex flex-col gap-0.5 w-full">
        {items.map((item) => {
          const hasSubitems = !!item.subitems;
          const key = parentKey + item.name;
          const isActive = isPathActive(item.path);
          const isOpen = openGroup[key];
          
          return (
            <li key={key} className="w-full">
              {hasSubitems ? (
                <>
                  <button
                    className={`flex items-center w-full justify-between px-3 py-1.5 rounded-md transition-colors text-[13px] font-medium
                      ${isOpen ? 'text-gray-900 bg-gray-100/50' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                    `}
                    onClick={() => handleCollapse(key)}
                    style={{ paddingLeft: `${0.75 + level * 1}rem` }}
                    type="button"
                  >
                    <div className="flex items-center gap-2.5">
                      {item.icon && <span className="text-[14px] opacity-70 flex-shrink-0">{item.icon}</span>}
                      <span className="truncate">{item.name}</span>
                    </div>
                    <span className="text-[10px] text-gray-400">
                      {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-[1000px] opacity-100 mt-0.5' : 'max-h-0 opacity-0'}`}
                  >
                    {renderMenuItems(item.subitems, key, level + 1)}
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md transition-all duration-150 text-[13px]
                    ${isActive 
                      ? 'bg-gray-200/60 text-gray-900 font-semibold' 
                      : 'text-gray-600 font-medium hover:bg-gray-100 hover:text-gray-900'
                    }
                  `}
                  style={{ paddingLeft: `${0.75 + level * 1}rem` }}
                  onClick={() => { if (isMobile) onClose(); }}
                >
                  {item.icon && <span className={`text-[14px] flex-shrink-0 ${isActive ? 'text-[#15803d]' : 'opacity-70'}`}>{item.icon}</span>}
                  <span className="truncate tracking-wide">{item.name}</span>
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      
      {open && isMobile && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
          onClick={onClose}
        />
      )}
      
      <aside
        className={`h-screen fixed left-0 top-0 z-40 transition-transform duration-300 bg-[#F9F9FB] border-r border-gray-200 flex flex-col no-print w-64
        ${isMobile ? (open ? 'translate-x-0' : '-translate-x-full') : ''}`}
      >
        <div className="flex items-center pt-3 pb-1 px-4 flex-shrink-0 relative border-b border-transparent">
          <div className="flex items-center w-full">
            <img src={ctmsLogo} alt="CTMS Logo" className="w-40 h-auto object-contain" />
          </div>
          
          {open && isMobile && (
            <button
              onClick={onClose}
              className="lg:hidden p-1.5 rounded-md hover:bg-gray-200 transition-colors text-gray-500 absolute right-3"
            >
              <FaTimes className="text-[13px]" />
            </button>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar px-3 py-2.5 pb-24" style={{ scrollbarGutter: 'stable', scrollbarWidth: 'none' }}>
          <div className="flex flex-col gap-6">
            {groups.map((group, idx) => (
              <div key={group.title} className="w-full">
                <h3 className="px-2 mb-2 text-[12px] font-bold tracking-wider text-gray-400 uppercase">
                  {group.title}
                </h3>
                {renderMenuItems(group.items || group.subitems, group.title, 0)}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Workspace Selector Style */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-[#F9F9FB] border-t border-gray-200">
          <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-100 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="w-6 h-6 rounded bg-[#15803d] flex items-center justify-center shrink-0">
                 <span className="text-white font-bold text-[10px]">{userName ? userName.charAt(0).toUpperCase() : 'U'}</span>
              </div>
              <div className="flex flex-col overflow-hidden">
                <span className="text-[12px] font-semibold text-gray-900 truncate">{userName}</span>
                <span className="text-[10px] text-gray-500 truncate capitalize">{userRole}</span>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleLogout();
              }}
              disabled={logoutLoading}
              className={`p-1.5 rounded-md transition-colors text-gray-400 hover:bg-gray-100 hover:text-red-500 ${logoutLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Logout"
            >
              {logoutLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaSignOutAlt className="text-[13px]" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
